import React, { useState } from 'react';
import {
  AppBar, Toolbar, Box, Button, useMediaQuery, useTheme,
  IconButton, Menu, MenuItem, Typography, Divider, Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircle from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import { APP_ROUTES } from '../constants/routes';
import { HEADER_TEST_ID, HEADER_TEXT } from '../constants/header';
import {
  USER_ACCESS_LEVEL,
  USER_ACCESS_LEVEL_LABEL,
  USER_ROLE,
  USER_ROLE_LABEL,
} from '../constants/userManagement';
import { AUTH_LOGOUT_REASON } from '../constants/auth';
import { useAuthActions, useAuthState } from '../store/authHooks';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, canWrite } = useAuthState();
  const { logout } = useAuthActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /** Opens the account menu anchored to the clicked user button. */
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /** Closes the account menu after a menu action or outside click. */
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  /** Closes the account menu and performs a manual logout. */
  const handleLogout = () => {
    setAnchorEl(null);
    logout(AUTH_LOGOUT_REASON.MANUAL);
  };

  /** Converts the current user role/access value into the label shown in the menu. */
  const getUserAccessLabel = () => {
    if (!user) return '';
    if (user.role === USER_ROLE.ADMIN) return USER_ROLE_LABEL[USER_ROLE.ADMIN];
    return user.access_level === USER_ACCESS_LEVEL.WRITE
      ? USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.WRITE]
      : USER_ACCESS_LEVEL_LABEL[USER_ACCESS_LEVEL.READ];
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'linear-gradient(90deg, #023e8a 0%, #0077b6 60%, #00b4d8 100%)',
        boxShadow: '0 2px 12px rgba(0,119,182,0.25)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 3 } }}>
        {/* Brand identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/OceanLogo.png"
            alt={HEADER_TEXT.LOGO_ALT}
            sx={{ height: isMobile ? 44 : 56, objectFit: 'contain' }}
          />
          {!isMobile && (
            <Box sx={{ lineHeight: 1.1 }}>
              <Box sx={{ color: '#fff', fontWeight: 800, fontSize: '1.15rem', letterSpacing: 1 }}>
                {HEADER_TEXT.BRAND_NAME}
              </Box>
              <Box sx={{ color: '#90e0ef', fontSize: '0.72rem', letterSpacing: 3 }}>
                {HEADER_TEXT.APP_NAME}
              </Box>
            </Box>
          )}
        </Box>

        {/* Nav */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            color="inherit"
            component={Link}
            to={APP_ROUTES.HOME}
            startIcon={<HomeIcon />}
            sx={{ borderRadius: 8, fontSize: isMobile ? '0.75rem' : '0.9rem' }}
          >
            {isMobile ? '' : HEADER_TEXT.DASHBOARD}
          </Button>

          {canWrite && (
            <Button
              variant="contained"
              component={Link}
              to={APP_ROUTES.ADD_BOOKING}
              startIcon={<PersonAddIcon />}
              sx={{
                background: 'linear-gradient(90deg,#52b788,#74c69d)',
                color: '#fff',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                '&:hover': { background: 'linear-gradient(90deg,#40916c,#52b788)' },
              }}
            >
              {isMobile ? '' : HEADER_TEXT.NEW_BOOKING}
            </Button>
          )}

          {/* User menu */}
          {user && (
            <>
              <Tooltip title={user.username}>
                <IconButton color="inherit" data-testid={HEADER_TEST_ID.USER_MENU_BUTTON} onClick={handleUserMenuOpen} size="small">
                  <AccountCircle />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography fontWeight={700} fontSize="0.9rem">{user.username}</Typography>
                  <Typography fontSize="0.75rem" color="text.secondary">
                    {getUserAccessLabel()}
                  </Typography>
                </Box>
                <Divider />
                {user.role === USER_ROLE.ADMIN && (
                  <MenuItem component={Link} to={APP_ROUTES.USER_MANAGEMENT} onClick={handleUserMenuClose}>
                    <PeopleIcon fontSize="small" sx={{ mr: 1 }} /> {HEADER_TEXT.USER_MANAGEMENT}
                  </MenuItem>
                )}
                <MenuItem component={Link} to={APP_ROUTES.CHANGE_PASSWORD} onClick={handleUserMenuClose}>
                  <VpnKeyIcon fontSize="small" sx={{ mr: 1 }} /> {HEADER_TEXT.CHANGE_PASSWORD}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> {HEADER_TEXT.LOGOUT}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
